import WarehouseSelector, { useActiveWarehouse, WarehouseSelectionRequired } from 'src/components/atoms/WarehouseSelector';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PageHeader from 'src/components/organisms/PageHeader';
import Footer from 'src/components/molecules/footer/Footer';
import ContentCardsContainer, { CardType, ContentCard } from 'src/components/organisms/ContentCard';
import { CustomDataGrid } from 'src/components/organisms/CustomDataGrid';
import { useDispatchOrderRowsTable, useDispatchOrdersTable, hasPickingListId } from 'src/hooks/table/configs/useDispatchOrdersTable';
import DetailPanelContainer from 'src/components/organisms/tables/DetailPanelContainer';
import { Alert, Box, Tooltip } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import CustomButton from 'src/components/atoms/buttons/CustomButton';
import OutboxIcon from '@mui/icons-material/Outbox';
import ArchiveTwoToneIcon from '@mui/icons-material/ArchiveTwoTone';
import DiscontinuedToggleButton from 'src/components/molecules/table/DiscontinuedToggleButton';
import { useSettingsContext } from 'src/hooks/useSettings';
import { getNestedValue } from 'src/utils/settings';
import { canUseDiscontinuedToggle } from 'src/utils/enviromentValues';
import { CollectivePickingListPrintJob, PickingListPrintJob, PickingListPrintResult, useDispatchPrintingSettings } from 'src/components/organisms/printing/PrintingManager';

const TABLE_NAME = "DispatchOrdersTable";

export const DispatchOrderDetailPanel = ({ row }) => {
    const [columns, tableSettings] = useDispatchOrderRowsTable(row?.row?.DispatchOrderId);

    return (
        <DetailPanelContainer>
            <CustomDataGrid columns={columns} tableSettings={tableSettings} />
        </DetailPanelContainer>
    );
};

const AutoPrintToggle = ({ enabled, onChange, status }) => (
    <Tooltip title={enabled ? 'Automatic picking list printing is enabled. Click to disable.' : 'Automatic picking list printing is disabled. Click to enable.'} arrow>
        <Box onClick={() => onChange(!enabled)} sx={{
            minWidth: 160, height: 48, px: 2.5,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            borderRadius: 1, cursor: 'pointer', userSelect: 'none',
            fontSize: '1rem', fontWeight: 700, lineHeight: 1.1,
            bgcolor: enabled ? 'success.main' : 'error.main',
            color: enabled ? 'success.contrastText' : 'error.contrastText',
            '&:hover': { bgcolor: enabled ? 'success.dark' : 'error.dark' },
        }}>
            <span>{enabled ? 'Printing Enabled' : 'Printing Disabled'}</span>
            <span style={{ fontSize: 11, fontWeight: 500, marginTop: 3, opacity: 0.9 }}>{status}</span>
        </Box>
    </Tooltip>
);

export const DispatchOrders = () => <WarehouseSelectionRequired><DispatchOrdersContent /></WarehouseSelectionRequired>;

const DispatchOrdersContent = () => {
    const { settings, setSetting } = useSettingsContext();
    const includeArchived = useMemo(() => getNestedValue(settings, ["IncludeDiscontinued", TABLE_NAME], false), [settings]);
    const printingSettings = useDispatchPrintingSettings();
    const { warehouseId, selectWarehouse } = useActiveWarehouse();
    const [autoPrintEnabled, setAutoPrintEnabled] = useState(false);
    const [dispatchOrdersRefreshKey, setDispatchOrdersRefreshKey] = useState(0);
    const [columns, tableSettings, tablePrinting, activeRows] = useDispatchOrdersTable(warehouseId, dispatchOrdersRefreshKey, includeArchived);

    type PrintJob = {
        key: number;
        warehouseId: number | string;
        settings: typeof printingSettings;
    } & (
        { type: 'single'; orderId: number | string } |
        { type: 'collective'; pickingListIds: string }
    );

    const [printJob, setPrintJob] = useState<PrintJob | null>(null);
    const [printResult, setPrintResult] = useState<PickingListPrintResult | null>(null);
    const [selectedPickingListCount, setSelectedPickingListCount] = useState(0);
    const printSequence = useRef(0);
    const printingRef = useRef(false);
    const [autoPrintPendingOrderId, setAutoPrintPendingOrderId] = useState<number | string | null>(null);
    const printing = printJob != null;
    const autoPrintOrderIds = useRef<Set<string>>(new Set());
    const apiRef = tableSettings.apiRef;

    const getSelectedPickingListIds = useCallback(() => {
        const selectedRows = apiRef.current?.getSelectedRows();
        if (!selectedRows || warehouseId == null) return [];

        return Array.from(new Set(
            Array.from(selectedRows.values())
                .filter((row) => hasPickingListId(row) && (row.WarehouseId == null || String(row.WarehouseId) === String(warehouseId)))
                .map((row) => String(row.PickingListId))
        ));
    }, [apiRef, warehouseId]);

    useEffect(() => {
        const updateSelection = () => setSelectedPickingListCount(getSelectedPickingListIds().length);
        updateSelection();
        return apiRef.current?.subscribeEvent('rowSelectionChange', updateSelection);
    }, [apiRef, getSelectedPickingListIds, tableSettings.allRows, includeArchived]);

    const startPrint = useCallback((orderId: number | string) => {
        if (warehouseId == null || orderId == null || printingRef.current) return;
        printingRef.current = true;
        setPrintResult(null);
        autoPrintOrderIds.current.add(`${warehouseId}:${orderId}`);
        setPrintJob({ type: 'single', key: ++printSequence.current, orderId, warehouseId, settings: printingSettings });
    }, [warehouseId, printingSettings]);

    const startCollectivePrint = useCallback(() => {
        if (warehouseId == null || printingRef.current) return;

        const pickingListIds = getSelectedPickingListIds();
        if (!pickingListIds.length) {
            setPrintResult({ error: 'Select at least one order with a picking list.' });
            return;
        }

        printingRef.current = true;
        setPrintResult(null);
        setAutoPrintPendingOrderId(null);
        setPrintJob({ type: 'collective', key: ++printSequence.current, pickingListIds: pickingListIds.join(','), warehouseId, settings: printingSettings });
    }, [warehouseId, printingSettings, getSelectedPickingListIds]);

    const finishPrint = useCallback((result: PickingListPrintResult) => {
        printingRef.current = false;
        setPrintResult(result);
        setPrintJob(null);
        setDispatchOrdersRefreshKey((current) => current + 1);
    }, []);

    // Keep manual jobs on this page coordinated with the automatic queue.
    const printingColumns = columns.map((column) => column.field !== 'PickingListId' ? column : {
        ...column,
        renderCell: (params) => <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CustomButton title="Print picking list" size="small" iconButton={true} variant="outlined" icon={<PrintIcon />} disabled={printing || warehouseId == null || params.row.DispatchOrderId == null || autoPrintPendingOrderId != null} onClick={(event) => {
                event.stopPropagation();
                startPrint(params.row.DispatchOrderId);
            }} />
            <span>{params.row.PickingListId ?? '-'}</span>
        </Box>,
    });

    useEffect(() => {
        if (warehouseId == null) return;
        const interval = window.setInterval(() => setDispatchOrdersRefreshKey((current) => current + 1), printingSettings.pickingList.refreshMs);
        return () => window.clearInterval(interval);
    }, [warehouseId, printingSettings.pickingList.refreshMs]);

    useEffect(() => {
        setAutoPrintPendingOrderId(null);
    }, [warehouseId]);

    const printableRows = (activeRows ?? []).filter((row) =>
        row.DispatchOrderId != null &&
        row.PickingListId == null &&
        row.DispatchOrderBlockId == null &&
        !autoPrintOrderIds.current.has(`${warehouseId}:${row.DispatchOrderId}`)
    );

    const autoPrintQueueLength = printableRows.length;
    const autoPrintStatus = !autoPrintEnabled ? 'Automatic printing off'
        : printing ? `Printing • ${autoPrintQueueLength} queued`
        : autoPrintPendingOrderId != null ? `Next print in ${printingSettings.pickingList.delayMs / 1000}s • ${autoPrintQueueLength} queued`
        : autoPrintQueueLength > 0 ? `${autoPrintQueueLength} queued`
        : 'No printing left';

    useEffect(() => {
        if (!autoPrintEnabled || warehouseId == null || printing || autoPrintPendingOrderId != null) return;

        const nextRow = [...(activeRows ?? [])].reverse().find((row) =>
            row.DispatchOrderId != null &&
            row.PickingListId == null &&
            row.DispatchOrderBlockId == null &&
            !autoPrintOrderIds.current.has(`${warehouseId}:${row.DispatchOrderId}`)
        );

        if (!nextRow) return;
        setAutoPrintPendingOrderId(nextRow.DispatchOrderId);
    }, [autoPrintEnabled, warehouseId, activeRows, printing, autoPrintPendingOrderId]);

    useEffect(() => {
        if (autoPrintPendingOrderId == null) return;

        if (!autoPrintEnabled || warehouseId == null) {
            setAutoPrintPendingOrderId(null);
            return;
        }

        if (printing) return;

        const pendingRow = (activeRows ?? []).find((row) => row.DispatchOrderId === autoPrintPendingOrderId);
        if (!pendingRow || pendingRow.PickingListId != null || pendingRow.DispatchOrderBlockId != null) {
            setAutoPrintPendingOrderId(null);
            return;
        }

        const timeout = window.setTimeout(() => {
            setAutoPrintPendingOrderId(null);
            startPrint(autoPrintPendingOrderId);
        }, printingSettings.pickingList.delayMs);

        return () => window.clearTimeout(timeout);
    }, [autoPrintPendingOrderId, autoPrintEnabled, warehouseId, printing, printingSettings, activeRows, startPrint]);

    const printNotice = printResult?.error ?? (printResult?.outcome && printResult.outcome.method !== 'printer'
        ? `${printResult.outcome.method === 'savePdf' ? 'PDF download requested.' : 'Printing skipped.'}${printResult.outcome.reason ? ` ${printResult.outcome.reason}` : ''}`
        : null);

    return (
        <>
            {tablePrinting}
            {printJob?.type === 'single' && <PickingListPrintJob key={printJob.key} orderId={printJob.orderId} warehouseId={printJob.warehouseId} settings={printJob.settings} onComplete={finishPrint} />}
            {printJob?.type === 'collective' && <CollectivePickingListPrintJob key={printJob.key} pickingListIds={printJob.pickingListIds} warehouseId={printJob.warehouseId} settings={printJob.settings} onComplete={finishPrint} />}
            {/* {printNotice && <Alert severity={printResult?.error ? 'error' : 'warning'} onClose={() => setPrintResult(null)} sx={{ mb: 2 }}>{printNotice}</Alert>} */}
            <PageHeader {...{
                title: "Dispatch Orders",
                subTitle: "View and print picking lists for dispatch orders",
                components: [
                    { component: <WarehouseSelector value={warehouseId} onChange={(value) => selectWarehouse(value)} /> },
                    ...(canUseDiscontinuedToggle ? [{ component: <DiscontinuedToggleButton settings={settings} setSetting={setSetting} tableName={TABLE_NAME} icon={<ArchiveTwoToneIcon />} tooltipOn="Hide archived" tooltipOff="Show archived" /> }] : []),
                    { component: <CustomButton title="Dispatch Station" icon={<OutboxIcon />} actionUrl="/warehouse/dispatch-orders/create" /> },
                    { component: <CustomButton title={`Print collective list (${selectedPickingListCount})`} icon={<PrintIcon />} disabled={printing || warehouseId == null || selectedPickingListCount === 0} onClick={startCollectivePrint} /> },
                    { isInGroupedMenu: true, component: <CustomButton title="Printing Settings" icon={<PrintIcon />} actionUrl="/settings/webclient" /> },
                    { position: "center", component: <AutoPrintToggle enabled={autoPrintEnabled} onChange={setAutoPrintEnabled} status={autoPrintStatus} /> },
                ]
            }} />

            <ContentCardsContainer>
                <ContentCard {...{ cardId: "DispatchOrdersPrinter", cardTypes: [CardType.noHeader], contentPadding: 0 }}>
                    <CustomDataGrid key={`${tableSettings.tableName}-${includeArchived ? "with-archived" : "active-only"}`} columns={printingColumns} tableSettings={tableSettings} getDetailPanelContent={(row) => <DispatchOrderDetailPanel row={row} />} />
                </ContentCard>
            </ContentCardsContainer>

            <Footer />
        </>
    );
};

export default DispatchOrders;
export { default as WarehouseFilter } from 'src/components/atoms/WarehouseSelector';
